import React from 'react';
import { Helmet } from "react-helmet";
import queryString from 'query-string';
import { request, prefCentreService } from '../functions/apiRequestHelper';
import { formatMobile, formatPostcode } from '../functions/textFormatting';
import { gtmPageview } from '../functions/gtm';
import Layout from '../components/Layout';
import AudiForm from '../components/AudiForm';
import Disclaimer from '../components/Disclaimer';
import Spinner from '../audi-ui-components/Spinner';
import DealerByPostcodeModule from '../components/DealerByPostcodeModule';
import ModelModule from '../components/ModelModule';
import MakeModule from '../components/MakeModule';
import {
    PATH_THANKS,
    FIELD_TEXT,
    FIELD_CHECKBOX,
    LABEL_OPTIN
} from '../constants';

const moduleComponents = {
    "MODULE_DEALER": DealerByPostcodeModule,
    "MODULE_MODEL": ModelModule,
    "MODULE_MAKE": MakeModule
};

class DynamicForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            user: {},
            isFetchingUser: true,
            loading: true,
            submitted: false,
            submittedData: {}
        }
    }

    componentDidMount() {
        const {match, location} = this.props;
        const slug = match.params.slug;
        const query = queryString.parse(location.search);
        if (location.pathname.endsWith(PATH_THANKS)) {
            let p = location.pathname.substring(0, location.pathname.length - PATH_THANKS.length)
            this.props.history.push(p + location.search);
        }
        if (query.sfid) {
            prefCentreService.get(query.sfid).then((result) => {
                this.setState({user: result, isFetchingUser: false});
                this.getFormData(slug, result);
            }).catch((error) => {
                console.error('user error', error);
                this.setState({isFetchingUser: false});
                this.getFormData(slug);
            });
        } else {
            this.setState({isFetchingUser: false});
            this.getFormData(slug);
        }
        gtmPageview(location.pathname);
    }

    getFormData = (slug, user) => {
        request(process.env.RAZZLE_API_HOST, { path: `1/form/${slug}` }).then(
            (data) => {
                // console.log(data);
                if (data.totalSeats && data.totalSeats > 0 && data.totalSeats - data.usedSeats < 1) {
                    data.formState = 'Closed';
                }
                var schema = {};
                // combine standard fields with dynamic fields
                var newFields = [];
                if (!data.fields) { data.fields = []; }
                if (data.excludeStandardFields && data.fields && data.fields.length) {
                    newFields = data.fields;
                } else {
                    newFields = [
                        {
                            field: FIELD_TEXT,
                            name: "firstName",
                            label: "First name",
                        },
                        {
                            field: FIELD_TEXT,
                            name: "lastName",
                            label: "Last name",
                        },
                        {
                            field: FIELD_TEXT,
                            name: "emailAddress",
                            label: data.checkMyAudiUser ? 'myAudi Email' : 'Email',
                        },
                        {
                            field: FIELD_TEXT,
                            name: "mobilePhone",
                            label: "Mobile phone",
                        },
                        ...data.fields,
                        {
                            field: FIELD_CHECKBOX,
                            name: "optIn",
                            label: LABEL_OPTIN
                        }
                    ];
                }
                // correct old field props, add user data, add modules, required validation
                const updateField = (_field, _schema) => {
                    if (_field.fieldType) { _field.field = _field.fieldType; }
                    if (_field.labelText) { _field.label = _field.labelText; }
                    if (_field.count) {
                        _field.showCount = true;
                        _field.maxLength = _field.count;
                    }
                    if (typeof _field.required !== "undefined") {
                        if (_field.required === true) {
                            _schema[_field.name] = {presence: {message: "This field is required", allowEmpty: false}};
                        } else {
                            _schema[_field.name] = {presence: false};
                        }
                    }
                    if (_field.moduleType) {
                        _field.module = moduleComponents[_field.moduleType];
                    }
                    if (_field.name === "mobilePhone") {
                        _field.formatValue = formatMobile;
                    }
                    if (_field.name === "postCode") {
                        _field.formatValue = formatPostcode;
                    }
                    if (user) {
                        if (_field.name === "firstName") { _field.value = user.firstName; }
                        if (_field.name === "lastName") { _field.value = user.lastName; }
                        if (_field.name === "emailAddress") { _field.value = user.email; }
                        if (_field.name === "mobilePhone") { _field.value = formatMobile(user.mobile); }
                        if (_field.name === "postCode") { _field.value = user.postcode; }
                    }
                    return [_field, _schema];
                }
                for (let i=0; i<newFields.length; i++) {
                    let field = newFields[i];
                    if (Array.isArray(field)) {
                        for (let ii=0; ii<field.length; ii++) {
                            let subField = field[ii];
                            let [updatedField, updatedSchema] = updateField(subField, schema);
                            newFields[i][ii] = updatedField;
                            schema = updatedSchema;
                        }
                    } else {
                        let [updatedField, updatedSchema] = updateField(field, schema);
                        newFields[i] = updatedField;
                        schema = updatedSchema;
                    }
                }
                data.fields = newFields;
                if (Object.keys(schema).length === 0) {
                    schema = null;
                }
                this.setState({
                    ...data,
                    schema,
                    loading: false
                });
            }
        ).catch(
            (error) => {
                console.error(error);
                this.setState({
                    formState: 'Error',
                    loading: false
                });
            }
        );
    }

    onSubmit = (values) => {
        const {location} = this.props;
        this.setState({submitted: true, submittedData: values});
        gtmPageview(`${location.pathname}${PATH_THANKS}`);
        this.props.history.push(`${location.pathname}${PATH_THANKS}${location.search}`.replace("//", "/"));
    }

    render() {
        const {match, location} = this.props;
        const query = queryString.parse(location.search);
        return (
            <Layout ui={query.ui} embedded={query.embedded} theme={query.theme}>
                <Helmet>
                    <title>Audi Australia</title>
                </Helmet>

                {this.state.heroImage && <div className="audi-forms__hero">
                    <div role="img" aria-label="" style={{background: `url(${this.state.heroImage}) no-repeat center top`, backgroundSize: 'cover', paddingTop: '42%', margin: '0 0 50px'}}></div>
                </div>}

                <main className="audi-forms__page">

                    {(this.state.loading || this.state.isFetchingUser) && <div className="spinner-wrapper"><Spinner isContinuous /></div>}

                    {this.state.submitted && <div>
                        {this.state.thankYouTitle && <h1 className="aui-headline-3">{this.state.thankYouTitle}</h1>}
                        {this.state.thankYouDescription && <div dangerouslySetInnerHTML={{__html: this.state.thankYouDescription}}></div>}
                    </div>}

                    {!this.state.submitted && !this.state.loading && <div>
                        {this.state.formState === 'Open' &&
                            <div>
                                {this.state.openTitle && <h1 className="aui-headline-3">{this.state.openTitle}</h1>}
                                {this.state.openDescription && <div dangerouslySetInnerHTML={{__html: this.state.openDescription}}></div>}

                                <AudiForm formName={this.state.code}
                                    fields={this.state.fields}
                                    schema={this.state.schema || null}
                                    preSubmit={(payload) => {
                                        if (payload.dealer) { payload.dealer = ""; }
                                        return payload;
                                    }}
                                    slug={match.params.slug}
                                    hiddenFields={this.state.hiddenFields || {}}
                                    submitLabel={this.state.submitLabel || "Submit"}
                                    onSubmitSuccess={this.onSubmit}
                                    style={{maxWidth: '840px'}}
                                    theme={query.theme}
                                />

                                <div className="pb-5">
                                    {this.state.privacyCollectionStatement &&
                                        <div dangerouslySetInnerHTML={{ __html: this.state.privacyCollectionStatement}} />
                                    }
                                    {!this.state.privacyCollectionStatement &&
                                        <Disclaimer />
                                    }
                                </div>

                            </div>
                        }

                        {this.state.formState === 'Closed' &&
                            <div>
                                {this.state.closedTitle && <h1>{this.state.closedTitle}</h1>}
                                {this.state.closedDescription && <div dangerouslySetInnerHTML={{__html: this.state.closedDescription}}></div>}
                            </div>
                        }

                        {this.state.formState === 'Error' &&
                            <div>
                                <h4>We are experiencing technical difficulties</h4>
                                <p>Please try again later.</p>
                            </div>
                        }
                    </div>}

                </main>
            </Layout>
        );
    }
}

export default DynamicForm;
